import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DateRangeFilter,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { MoneyDisplay } from '@/components/shared/money-display';
import { Pagination } from '@/components/shared/pagination';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatMoney, formatNumber } from '@/utils/format';

import { downloadReportCsv } from '../reports/download-report-csv';
import { downloadReportPdfExport } from '../reports/download-report-pdf';
import { formatReportCellValue, reportRowIsLiability } from '../reports/format-report-export';
import {
  activeReportFilterQuery,
  ReportFiltersBar,
  type ReportFilterDefinition,
} from '../reports/report-filters-bar';
import { ReportExportMenu } from '../reports/report-export-menu';
import { ReportRunSkeleton } from '../reports/report-run-skeleton';

const REPORT_KEY = 'balance_sheet';

const BALANCE_SHEET_FILTERS: ReportFilterDefinition[] = [
  { key: 'currency', kind: 'currency', labelKey: 'filter.currency' },
];

interface ReportColumn {
  key: string;
  type: 'string' | 'number' | 'money' | 'date';
}

interface BalanceSheetRunResult {
  reportKey: string;
  columns: ReportColumn[];
  omittedColumns: string[];
  rows: Record<string, unknown>[];
  page: { page: number; pageSize: number; totalItems: number; totalPages: number };
  totals?: {
    currency?: string;
    totalAssetsMinor?: number;
    totalLiabilitiesMinor?: number;
    totalNetAssetsMinor?: number;
    isBalanced?: boolean;
    differenceMinor?: number;
  };
}

export const BALANCE_SHEET_REPORT_QUERY_KEY = ['reports', 'run', REPORT_KEY] as const;

export const BalanceSheetReportPanel = () => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);
  const canExport = hasPermission(membership, PERMISSIONS.REPORTS_EXPORT);
  const churchId = church?.id;

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [page, setPage] = useState(1);
  const [reportFilters, setReportFilters] = useState<Record<string, string>>({});

  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;
  const activeFilters = useMemo(
    () => activeReportFilterQuery(BALANCE_SHEET_FILTERS, reportFilters),
    [reportFilters],
  );

  const run = useQuery({
    queryKey: [...BALANCE_SHEET_REPORT_QUERY_KEY, dateFrom, dateTo, page, activeFilters],
    refetchOnWindowFocus: false,
    queryFn: () =>
      api.get<BalanceSheetRunResult>(`/reports/${REPORT_KEY}/run`, {
        query: {
          page,
          pageSize: 100,
          dateFrom: dateFrom ?? undefined,
          dateTo: dateTo ?? undefined,
          ...activeFilters,
        },
      }),
  });

  const exportReport = useMutation({
    mutationFn: async (format: 'csv' | 'pdf') => {
      if (!churchId) return;
      if (format === 'csv') {
        await downloadReportCsv({
          churchId,
          reportKey: REPORT_KEY,
          dateFrom: dateFrom ?? undefined,
          dateTo: dateTo ?? undefined,
          filters: activeFilters,
        });
        return;
      }
      await downloadReportPdfExport({
        reportKey: REPORT_KEY,
        reportTitle: t('seeded.balanceSheet.name'),
        reportDescription: t('seeded.balanceSheet.description'),
        churchName: church?.name ?? t('title'),
        branchName: church?.campusName,
        logoUrl: church?.logoUrl,
        baseCurrency: church?.baseCurrency ?? 'GHS',
        dateFrom: dateFrom ?? undefined,
        dateTo: dateTo ?? undefined,
        filters: activeFilters,
        supportsDateRange: true,
        locale,
        timezone,
        t,
        tCommon,
      });
    },
    onSuccess: (_data, format) => {
      toast.success(format === 'csv' ? t('run.exportDownloaded') : t('run.exportPdfDownloaded'));
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  const formatCell = (column: ReportColumn, value: unknown, row: Record<string, unknown>) => {
    if (value === null || value === undefined || value === '') return '—';
    if (column.type === 'money') {
      const currency = typeof row.currency === 'string' ? row.currency : 'GHS';
      return (
        <MoneyDisplay
          amount={Number(value)}
          currency={currency}
          colorNegative
          className={reportRowIsLiability(row) ? 'text-danger-emphasis' : undefined}
        />
      );
    }
    if (column.type === 'number') return formatNumber(Number(value));
    return formatReportCellValue(column, value, row, t, { locale });
  };

  const visibleColumns = useMemo(() => {
    if (!run.data?.rows.length) return run.data?.columns ?? [];
    const currencies = new Set(
      run.data.rows
        .map((row) => row.currency)
        .filter((value): value is string => typeof value === 'string' && value.length > 0),
    );
    if (currencies.size <= 1) {
      return run.data.columns.filter((column) => column.key !== 'currency');
    }
    return run.data.columns;
  }, [run.data?.columns, run.data?.rows]);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 py-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex min-w-0 flex-1 flex-wrap items-end gap-2">
              <DateRangeFilter
                value={dateRange}
                onChange={(range) => {
                  setDateRange(range);
                  setPage(1);
                }}
                labels={{
                  today: tCommon('date.today'),
                  yesterday: tCommon('date.yesterday'),
                  thisWeek: tCommon('date.thisWeek'),
                  lastWeek: tCommon('date.lastWeek'),
                  thisMonth: tCommon('date.thisMonth'),
                  lastMonth: tCommon('date.lastMonth'),
                  thisQuarter: tCommon('date.thisQuarter'),
                  thisYear: tCommon('date.thisYear'),
                  lastYear: tCommon('date.lastYear'),
                  allTime: tCommon('date.allTime'),
                  custom: tCommon('date.custom'),
                  from: t('run.dateFrom'),
                  to: t('run.dateTo'),
                }}
              />
              <ReportFiltersBar
                filters={BALANCE_SHEET_FILTERS}
                values={reportFilters}
                onChange={(key, value) => {
                  setReportFilters((previous) => ({ ...previous, [key]: value }));
                  setPage(1);
                }}
              />
            </div>
            <div className="flex shrink-0 justify-end gap-2">
              {canExport ? (
                <ReportExportMenu
                  loading={exportReport.isPending}
                  format={exportReport.variables}
                  onExport={(format) => exportReport.mutate(format)}
                />
              ) : null}
              <Button onClick={() => void run.refetch()} loading={run.isFetching}>
                {t('run.run')}
              </Button>
            </div>
          </div>
          <Tip tipId="reports.dateRange" />
        </CardContent>
      </Card>

      {run.isFetching ? (
        <ReportRunSkeleton summaryCards={3} columnCount={visibleColumns.length || 3} />
      ) : run.isError ? (
        <Card>
          <CardContent className="py-6 text-body-sm text-danger-fg">
            {tCommon('state.errorBody')}
          </CardContent>
        </Card>
      ) : run.data ? (
        <>
          {run.data.totals?.totalAssetsMinor !== undefined ? (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  { label: t('totals.totalAssets'), amount: run.data.totals.totalAssetsMinor },
                  {
                    label: t('totals.totalLiabilities'),
                    amount: run.data.totals.totalLiabilitiesMinor,
                    liability: true,
                  },
                  {
                    label: t('totals.totalNetAssets'),
                    amount: run.data.totals.totalNetAssetsMinor,
                  },
                ].map((item) => (
                  <Card key={item.label}>
                    <CardContent className="py-4">
                      <p className="text-caption text-fg-secondary">{item.label}</p>
                      <MoneyDisplay
                        amount={Number(item.amount ?? 0)}
                        currency={run.data.totals?.currency ?? 'GHS'}
                        size="lg"
                        colorNegative
                        className={item.liability ? 'text-danger-emphasis' : undefined}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-body-sm text-fg-muted">
                {run.data.totals.isBalanced
                  ? t('totals.balanced')
                  : t('totals.unbalanced', {
                      amount: formatMoney(
                        Math.abs(Number(run.data.totals.differenceMinor ?? 0)),
                        run.data.totals.currency ?? 'GHS',
                      ),
                    })}
              </p>
            </div>
          ) : null}

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full table-fixed text-body-sm">
                <thead>
                  <tr className="border-b border-border text-left text-caption text-fg-secondary">
                    {visibleColumns.map((column) => (
                      <th
                        key={column.key}
                        className={`px-4 py-3 font-medium ${
                          column.type === 'money' || column.type === 'number' ? 'text-right' : ''
                        } ${
                          column.key === 'lineLabel'
                            ? 'w-[40%]'
                            : column.key === 'currency'
                              ? 'w-24'
                              : column.type === 'money'
                                ? 'w-36'
                                : ''
                        }`}
                      >
                        {t(`column.${column.key}`, column.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {run.data.rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleColumns.length}
                        className="px-4 py-8 text-center text-fg-muted"
                      >
                        {tCommon('table.noResults')}
                      </td>
                    </tr>
                  ) : (
                    run.data.rows.map((row, index) => (
                      <tr key={index} className="border-b border-border last:border-0">
                        {visibleColumns.map((column) => (
                          <td
                            key={column.key}
                            className={cn(
                              'px-4 py-3 align-top',
                              (column.type === 'money' || column.type === 'number') &&
                                'text-right tabular-nums whitespace-nowrap',
                            )}
                          >
                            {formatCell(column, row[column.key], row)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Pagination
            page={run.data.page.page}
            pageSize={run.data.page.pageSize}
            totalItems={run.data.page.totalItems}
            totalPages={run.data.page.totalPages}
            onPageChange={setPage}
            labels={{
              showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </>
      ) : null}
    </div>
  );
};
